import React, { FC, useState, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { Book } from 'react-bootstrap-icons';

import { ITranslatedFile, ICmsArticleCountryListItem } from 'api/types';
import { FormElement, Modal, Button } from 'components/controls';

import styles from 'theme/components/controls/TranslatedFile/TranslatedFile.module.scss';

// typ danych wejściowych
interface IProps {
  name: string;
  selectedCountry: string;
  file: ITranslatedFile[];
  setFieldValue: (arg1: string, arg2: ITranslatedFile[]) => void;
  countries: ICmsArticleCountryListItem[];
}

const TranslatedInput: FC<IProps> = ({ name, file, selectedCountry, setFieldValue, countries }) => {
  const { t } = useTranslation();

  const [formValues, setFormValues] = useState(file);
  const [open, setOpen] = useState(false);

  const [mimeType, setMimeType] = useState('image/png');

  useEffect(() => {
    if (!file || !file.length) {
      const newValues = countries.map((country) => ({
        language: country.code,
        base64: '',
        name: ''
      }));

      setFormValues(newValues);

      // ustawianie aktywnego tabu
      setActiveTab(newValues?.[0]?.language);
    }
  }, [file, countries]);

  // lista tabów
  const tabs =
    formValues?.map((o) => ({
      key: o.language,
      label: o.language
    })) || [];

  // rodzaj aktywnej listy
  const [activeTab, setActiveTab] = useState<string>(tabs?.[0]?.key || '');

  const currentLanguageValue = useMemo(
    () => formValues?.find((o) => o.language === selectedCountry)?.language,
    [formValues, selectedCountry]
  );

  const handleTabChange = (tab: string) => {
    // ustawienie delay, żeby odmontować i zamontować komponenty
    setTimeout(() => {
      setActiveTab(tab);
    }, 10);
  };

  const base64ToBlobUrl = (base64: string) => {
    const byteString = atob(base64.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([ab], { type: mimeType });
    return URL.createObjectURL(blob);
  };

  const onChangeHandler = (file?: File, language?: string, onSave?: boolean) => {
    if (!file || !language) {
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function () {
      if (formValues && Array.isArray(formValues)) {
        const newValue: ITranslatedFile[] = formValues.map((o) => {
          if (o.language === language) {
            return {
              ...o,
              base64: reader.result as string,
              name: file.name,
              language: language
            };
          }
          return o;
        });
        setFormValues(newValue);

        if (onSave) {
          setFieldValue(name, newValue);
        }

        const mimeMatch = (reader.result as string).match(/^data:(.*?);base64,/);
        const mimeType = mimeMatch ? mimeMatch[1] : undefined;

        mimeType && setMimeType(mimeType);
      }
    };
  };

  const handleSave = () => {
    setOpen(false);
    formValues && setFieldValue(name, formValues);
  };

  const renderActiveComponent = () => {
    const selectedValue = formValues?.find((o) => o.language === activeTab);

    return (
      <div className={styles.activeComponent}>
        <label htmlFor="activeComponentFile">
          <span className={styles.fileButton}>Wybierz plik</span>
          <div>{selectedValue?.name || ''}</div>
        </label>

        <input
          id="activeComponentFile"
          onChange={({ target: { files } }) => {
            onChangeHandler(files?.[0], selectedValue?.language);
          }}
          type="file"
        />

        <img
          src={selectedValue?.base64 ? base64ToBlobUrl(selectedValue.base64) : selectedValue?.url}
        />
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div className={styles.content}>
        <div className="tabs">
          {tabs.map((tab) => (
            <div
              key={tab.key}
              className={classnames('tab', {
                active: tab.key === activeTab
              })}
              onClick={() => (setActiveTab(''), handleTabChange(tab.key))}>
              {tab.label}
            </div>
          ))}
        </div>
        <div className="contentWrapper">{renderActiveComponent()}</div>
      </div>
    );
  };

  return (
    <div className={styles.componentWrapper}>
      <FormElement>
        <div className={styles.inputWrapper}>
          <div className={styles.input}>
            <label htmlFor="file">
              <span className={styles.fileButton}>Wybierz plik</span>
              <div>{formValues?.find((o) => o.language === selectedCountry)?.name}</div>
            </label>
            <input
              id="file"
              onChange={({ target: { files } }) => {
                onChangeHandler(files?.[0], currentLanguageValue, true);
              }}
              type="file"
            />
          </div>
          <Button onClick={() => setOpen(true)} ghost color="secondary">
            <Book size="18px" />
          </Button>
        </div>
        <div>
          <img
            className={styles.thumbnail}
            src={
              formValues?.find((o) => o.language === selectedCountry)?.base64
                ? base64ToBlobUrl(
                    formValues?.find((o) => o.language === selectedCountry)?.base64 || ''
                  )
                : formValues?.find((o) => o.language === selectedCountry)?.url
            }
          />
        </div>
      </FormElement>
      {open && (
        <Modal fullWidth onClose={() => setOpen(false)} title={t('Słownik')}>
          {renderContent()}
          <div className={styles.actionWrapper}>
            <Button onClick={() => handleSave()}>
              <Trans>Zapisz</Trans>
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TranslatedInput;
